// client/src/components/ticketing/EventLandingPage.jsx
//
// Public, unauthenticated event registration/ticketing page reached via
// /e/:tenantSlug/:eventId/:itemId. Intentionally renders WITHOUT
// AppHeader/Sidebar/AppFooter — modeled directly on
// client/src/components/media/ExternalUploadPage.jsx's top-level-route,
// explicit-page-state pattern (loading/ready/not_found/error), which is the
// established template for a standalone public page in this app.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import usePromoterRef from '../../hooks/usePromoterRef';
import axios from 'axios';
import TicketPurchasePanel from './TicketPurchasePanel';
import PriceNote from './PriceNote';
import { calendarLinks } from '../../utils/calendarLinks';
import { formatCurrency } from '../../utils/currencyUtils';
import { COUNTRIES } from '../../utils/countryUtils';
import CustomField from './CustomField';
import { assetUrl } from '../../utils/assetUrl';
import { useTranslation } from 'react-i18next';

const STATE_LOADING = 'loading';
const STATE_READY = 'ready';
const STATE_NOT_FOUND = 'not_found';
const STATE_ERROR = 'error';

function formatDateRange(startdate, enddate) {
  if (!startdate) return '';
  try {
    const start = new Date(startdate);
    const end = enddate ? new Date(enddate) : null;
    const opts = { dateStyle: 'medium', timeStyle: 'short' };
    const startLabel = start.toLocaleString(undefined, opts);
    if (!end) return startLabel;
    return `${startLabel} – ${end.toLocaleString(undefined, opts)}`;
  } catch (err) {
    console.warn('formatDateRange: could not parse date', err);
    return startdate;
  }
}

export default function EventLandingPage() {
  const { tenantSlug, eventId, itemId } = useParams();
  usePromoterRef({ tenantSlug, eventId, itemId }); // ?ref=CODE → promoter credit
  const [pageState, setPageState] = useState(STATE_LOADING);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const [selectedTierId, setSelectedTierId] = useState(null);
  const [buyer, setBuyer] = useState({ name: '', email: '', phone: '', attributes: {} });
  const [promoCode, setPromoCode] = useState('');
  const [buyerCountry, setBuyerCountry] = useState('');
  const [buyerPostalCode, setBuyerPostalCode] = useState('');
  // Code-only tiers the buyer has unlocked with an access code (Growth Phase 3).
  const [unlockedTiers, setUnlockedTiers] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: resp } = await axios.get(`/api/ticketing/public/${tenantSlug}/${eventId}/${itemId}`);
        if (cancelled) return;
        setData(resp);
        const firstAvailable = (resp.tiers || []).find((t) => !t.soldOut);
        setSelectedTierId(firstAvailable ? firstAvailable.id : (resp.tiers?.[0]?.id || null));
        setPageState(STATE_READY);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) setPageState(STATE_NOT_FOUND);
        else {
          setError(err?.response?.data?.message || 'Something went wrong loading this event.');
          setPageState(STATE_ERROR);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tenantSlug, eventId, itemId]);

  const allTiers = useMemo(
    () => [...(data?.tiers || []), ...unlockedTiers.filter((u) => !(data?.tiers || []).some((t) => t.id === u.id))],
    [data, unlockedTiers]
  );

  const selectedTier = useMemo(
    () => allTiers.find((t) => t.id === selectedTierId) || null,
    [allTiers, selectedTierId]
  );

  const onUnlocked = (tier, code) => {
    setUnlockedTiers((list) => [...list.filter((x) => x.id !== tier.id), tier]);
    setSelectedTierId(tier.id);
    setPromoCode(code);
  };

  const requiredFieldsFilled = useMemo(() => {
    if (!data) return false;
    if (!buyer.name.trim() || !/^\S+@\S+\.\S+$/.test(buyer.email)) return false;
    if (data.tenant?.taxEnabled && !buyerCountry) return false;
    for (const field of data.fields || []) {
      if (!field.required) continue;
      const v = buyer.attributes[field.field_key];
      if (v === undefined || v === null || v === '' || v === false) return false;
    }
    return true;
  }, [data, buyer, buyerCountry]);

  const setAttribute = (key, value) => {
    setBuyer((b) => ({ ...b, attributes: { ...b.attributes, [key]: value } }));
  };

  if (pageState === STATE_LOADING) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm opacity-70 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100">
        Loading event…
      </div>
    );
  }

  if (pageState === STATE_NOT_FOUND) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-4 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100">
        <div>
          <h1 className="text-xl font-semibold mb-2">Event not found</h1>
          <p className="text-sm opacity-70">This event may no longer be accepting registrations.</p>
        </div>
      </div>
    );
  }

  if (pageState === STATE_ERROR) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-4 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100">
        <div>
          <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
          <p className="text-sm opacity-70">{error}</p>
        </div>
      </div>
    );
  }

  const { tenant, event, item, fields } = data;
  const tiers = allTiers;

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-6">
          {tenant.logo && (
            <img src={assetUrl(tenant.logo)} alt={tenant.companyname} className="w-10 h-10 rounded object-contain bg-white" />
          )}
          <span className="font-semibold opacity-80">{tenant.companyname}</span>
        </div>

        <h1 className="text-2xl font-bold mb-1">{event.name}</h1>
        {item.name && item.name !== event.name && (
          <div className="text-sm opacity-70 mb-1">{item.name}</div>
        )}
        <div className="text-sm opacity-70 mb-2">{formatDateRange(event.startdate, event.enddate)}</div>
        <AddToCalendar
          tenantSlug={tenantSlug}
          eventId={event.id}
          itemId={item.id}
          name={event.name}
          description={event.description}
          start={event.startdate}
          end={event.enddate}
        />
        {event.description && <p className="mb-6 mt-4 opacity-90">{event.description}</p>}

        <h2 className="font-semibold mb-2">Select a ticket</h2>
        <div className="space-y-2 mb-6">
          {tiers.map((tier) => (
            <label
              key={tier.id}
              aria-label={`${tier.name}${tier.soldOut ? ' (sold out)' : ` — ${formatCurrency(tier.price, tier.currency)}`}`}
              className={`flex items-center justify-between gap-3 rounded-xl border p-3 cursor-pointer ${
                selectedTierId === tier.id ? 'border-cobalt' : 'border-black/10 dark:border-white/10'
              } ${tier.soldOut ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="tier"
                  checked={selectedTierId === tier.id}
                  disabled={tier.soldOut}
                  onChange={() => setSelectedTierId(tier.id)}
                />
                <div>
                  <div className="font-semibold">{tier.name}</div>
                  <div className="text-xs opacity-70">
                    {tier.soldOut ? 'Sold out' : formatCurrency(tier.price, tier.currency)}
                    {!tier.soldOut && tier.phase && <> · {tier.phase.label}</>}
                  </div>
                  {!tier.soldOut && <PriceNote tier={tier} />}
                  {tier.description && <div className="text-xs opacity-80 mt-1">{tier.description}</div>}
                </div>
              </div>
            </label>
          ))}
          {tiers.length === 0 && <div className="text-sm opacity-70">No tickets are currently available.</div>}
        </div>

        {data.hasCodeOnlyTiers && (
          <UnlockForm tenantSlug={tenantSlug} eventId={event.id} itemId={item.id} onUnlocked={onUnlocked} />
        )}

        {tiers.some((t) => t.soldOut) && (
          <WaitlistForm
            tenantSlug={tenantSlug}
            eventId={event.id}
            itemId={item.id}
            soldOutTiers={tiers.filter((t) => t.soldOut)}
          />
        )}

        {selectedTier && (
          <>
            <h2 className="font-semibold mb-2">Your information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <input
                className="px-3 py-2 rounded border bg-transparent"
                placeholder="Full name"
                value={buyer.name}
                onChange={(e) => setBuyer((b) => ({ ...b, name: e.target.value }))}
                required
              />
              <input
                className="px-3 py-2 rounded border bg-transparent"
                type="email"
                placeholder="Email"
                value={buyer.email}
                onChange={(e) => setBuyer((b) => ({ ...b, email: e.target.value }))}
                required
              />
              <input
                className="px-3 py-2 rounded border bg-transparent sm:col-span-2"
                placeholder="Phone (optional)"
                value={buyer.phone}
                onChange={(e) => setBuyer((b) => ({ ...b, phone: e.target.value }))}
              />
            </div>

            {tenant.taxEnabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <select
                  className="px-3 py-2 rounded border bg-transparent"
                  value={buyerCountry}
                  onChange={(e) => setBuyerCountry(e.target.value)}
                  required
                >
                  <option value="">Country *</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
                <input
                  className="px-3 py-2 rounded border bg-transparent"
                  placeholder="Postal / ZIP code"
                  value={buyerPostalCode}
                  onChange={(e) => setBuyerPostalCode(e.target.value)}
                />
              </div>
            )}

            {fields.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                {fields.map((field) => (
                  <CustomField key={field.id} field={field} value={buyer.attributes[field.field_key]} onChange={(v) => setAttribute(field.field_key, v)} />
                ))}
              </div>
            )}

            <input
              className="w-full px-3 py-2 rounded border bg-transparent mb-4 uppercase"
              placeholder="Promo code (optional)"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
            />

            <TicketPurchasePanel
              tenantId={tenant.id}
              eventId={event.id}
              itemId={item.id}
              tier={selectedTier}
              buyer={buyer}
              buyerValid={requiredFieldsFilled}
              promoCode={promoCode}
              buyerCountry={tenant.taxEnabled ? buyerCountry : null}
              buyerPostalCode={tenant.taxEnabled ? buyerPostalCode : null}
            />
          </>
        )}
      </div>
    </div>
  );
}

function AddToCalendar({ tenantSlug, eventId, itemId, name, description, start, end }) {
  if (!start) return null;
  const icsUrl = `/api/ticketing/public/${tenantSlug}/${eventId}/${itemId}/calendar.ics`;
  const links = calendarLinks({ name, description, start, end, icsUrl });
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <a href={links.ics} className="px-2.5 py-1 rounded border border-black/15 dark:border-white/15">Add to calendar</a>
      {links.google && <a href={links.google} target="_blank" rel="noreferrer" className="px-2.5 py-1 rounded border border-black/15 dark:border-white/15">Google</a>}
      {links.outlook && <a href={links.outlook} target="_blank" rel="noreferrer" className="px-2.5 py-1 rounded border border-black/15 dark:border-white/15">Outlook</a>}
    </div>
  );
}

// "Have an access code?" — unlocks a code-only tier (Growth Phase 3). The
// server returns the tier and the normalized code, which becomes the promo
// code sent at checkout (purchase-intent re-checks it).
function UnlockForm({ tenantSlug, eventId, itemId, onUnlocked }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [state, setState] = useState('idle'); // idle | checking | done | error
  const [msg, setMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setState('checking');
    setMsg('');
    try {
      const { data } = await axios.post(`/api/ticketing/public/${tenantSlug}/${eventId}/${itemId}/unlock`, { code: code.trim() });
      onUnlocked(data.tier, data.code);
      setState('done');
      setMsg(t('ticketing.landing.unlocked', '{{tier}} is now available below.', { tier: data.tier.name }));
    } catch (err) {
      setState('error');
      setMsg(err?.response?.data?.message || t('ticketing.landing.unlockFailed', "That code didn't unlock any tickets"));
    }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-6 space-y-2">
      <label htmlFor="access_code" className="block text-sm font-semibold">
        {t('ticketing.landing.haveCode', 'Have an access code?')}
      </label>
      <div className="flex gap-2">
        <input
          id="access_code"
          className="flex-1 min-w-0 px-3 py-2 rounded border bg-transparent text-sm uppercase"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="off"
        />
        <button type="submit" disabled={state === 'checking'} className="px-3 py-2 rounded bg-cobalt text-white text-sm disabled:opacity-50">
          {state === 'checking' ? t('ticketing.landing.checking', 'Checking…') : t('ticketing.landing.unlock', 'Unlock')}
        </button>
      </div>
      {msg && (
        <div className={`text-xs ${state === 'error' ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-300'}`} role="status">
          {msg}
        </div>
      )}
    </form>
  );
}

function WaitlistForm({ tenantSlug, eventId, itemId, soldOutTiers }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [tierId, setTierId] = useState(soldOutTiers[0]?.id ? String(soldOutTiers[0].id) : '');
  const [state, setState] = useState('idle'); // idle | saving | done | error
  const [msg, setMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setState('saving');
    setMsg('');
    try {
      const { data } = await axios.post(`/api/ticketing/public/${tenantSlug}/${eventId}/${itemId}/waitlist`, {
        name, email, tier_id: tierId ? Number(tierId) : null,
      });
      setState('done');
      setMsg(data.message || "You're on the waitlist.");
    } catch (err) {
      setState('error');
      setMsg(err?.response?.data?.message || 'Could not join the waitlist.');
    }
  };

  if (state === 'done') {
    return <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm mb-6">{msg}</div>;
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-6 space-y-2">
      <div className="text-sm font-semibold">Some tickets are sold out — join the waitlist</div>
      {soldOutTiers.length > 1 && (
        <select value={tierId} onChange={(e) => setTierId(e.target.value)} className="w-full px-3 py-2 rounded border bg-transparent text-sm">
          {soldOutTiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input className="px-3 py-2 rounded border bg-transparent text-sm" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required />
        <input className="px-3 py-2 rounded border bg-transparent text-sm" type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </div>
      {state === 'error' && <div className="text-xs text-red-600">{msg}</div>}
      <button type="submit" disabled={state === 'saving'} className="px-3 py-2 rounded bg-cobalt text-white text-sm disabled:opacity-50">
        {state === 'saving' ? 'Joining…' : 'Join waitlist'}
      </button>
    </form>
  );
}

